import React, { useEffect, useMemo, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import { useHistoryMetrics } from '../../hooks/useHistoryMetrics.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const HistoryMetricsSection = ({ onNotify }) => {
  const {
    filters,
    draftFilters,
    rows,
    summary,
    loading,
    error,
    loadMetrics,
    setFilterValue,
    exportMetrics,
    trends,
    catalogs
  } = useHistoryMetrics();

  useLucideIcons([
    rows.length,
    summary.totalClicks,
    loading,
    filters.aggregate,
    trends.labels?.length,
    trends.divisionBreakdown?.length,
    trends.areaBreakdown?.length,
    trends.processTypeBreakdown?.articulos?.length,
    trends.processTypeBreakdown?.recetas?.length,
    filters.divisionId,
    filters.areaId
  ]);

  const handleFiltersSubmit = async (event) => {
    event.preventDefault();
    try {
      await loadMetrics();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron aplicar los filtros.' });
    }
  };

  const handleExport = async () => {
    try {
      await exportMetrics();
      onNotify?.({ type: 'success', message: 'Se inició la descarga del archivo.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo exportar el archivo.' });
    }
  };

  const processColumns = useMemo(() => {
    const names = new Set();
    rows.forEach((row) => {
      const counts = row?.procesos_seleccionados_counts;
      if (counts && typeof counts === 'object' && !Array.isArray(counts)) {
        Object.keys(counts).forEach((name) => {
          if (name) {
            names.add(name);
          }
        });
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const tableColumns = useMemo(() => {
    const columns = [];
    if (!filters.aggregate) {
      columns.push({ key: 'fecha', label: 'Fecha', getValue: (row) => row.fecha || '' });
    }
    columns.push(
      { key: 'user_name', label: 'Usuario', getValue: (row) => row.user_name || 'Sin nombre' },
      { key: 'sucursal_nombre', label: 'Restaurante', getValue: (row) => row.sucursal_nombre || '-' },
      { key: 'articulos', label: 'Click Artículos', getValue: (row) => row.articulos || 0 },
      { key: 'recetas', label: 'Click Recetas', getValue: (row) => row.recetas || 0 },
      { key: 'total', label: 'Total', getValue: (row) => row.total || 0 },
      ...processColumns.map((processName) => ({
        key: `process:${processName}`,
        label: processName,
        getValue: (row) => {
          const count = row?.procesos_seleccionados_counts?.[processName];
          return Number.isFinite(Number(count)) ? Number(count) : 0;
        }
      })),
      {
        key: 'procesos_seleccionados',
        label: 'Procesos seleccionados',
        getValue: (row) => {
          const procesos = row.procesos_seleccionados || [];
          return Array.isArray(procesos) ? procesos.join(', ') : '';
        },
        exportOnly: true
      }
    );
    return columns;
  }, [filters.aggregate, processColumns]);

  const tableColumnsVisible = tableColumns.filter(col => !col.exportOnly);
  const tableHeaders = tableColumnsVisible.map((column) => column.label);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const filteredRows = useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLowerCase();
    if (!normalizedQuery) {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [
        row.fecha,
        row.user_name,
        row.sucursal_nombre,
        row.rbi_id
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [rows, searchTerm]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, filteredRows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredRows.length, filters.aggregate, filters.startDate, filters.endDate, searchTerm]);

  const filteredAreas = useMemo(() => {
    if (!draftFilters.divisionId) {
      return catalogs.areas;
    }
    const selectedDivision = catalogs.divisions.find(
      (division) => String(division.boh) === String(draftFilters.divisionId)
    );
    if (!selectedDivision) {
      return [];
    }
    return catalogs.areas.filter((area) => Number(area.division_id) === Number(selectedDivision.id));
  }, [catalogs.areas, catalogs.divisions, draftFilters.divisionId]);

  useEffect(() => {
    if (!draftFilters.areaId) {
      return;
    }
    const currentArea = catalogs.areas.find((area) => String(area.boh) === String(draftFilters.areaId));
    if (!currentArea) {
      setFilterValue('areaId', '');
      return;
    }
    if (draftFilters.divisionId) {
      const selectedDivision = catalogs.divisions.find(
        (division) => String(division.boh) === String(draftFilters.divisionId)
      );
      if (!selectedDivision || String(currentArea.division_id) !== String(selectedDivision.id)) {
        setFilterValue('areaId', '');
      }
    }
  }, [catalogs.areas, catalogs.divisions, draftFilters.areaId, draftFilters.divisionId, setFilterValue]);

  const handleTableExport = () => {
    if (!rows.length) {
      onNotify?.({ type: 'error', message: 'No hay filas para exportar.' });
      return;
    }

    const escapeCsv = (value) => {
      const text = value === null || value === undefined ? '' : String(value);
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const lines = [
      tableColumns.map((column) => escapeCsv(column.label)).join(',')
    ];

    rows.forEach((row) => {
      const line = tableColumns.map((column) => escapeCsv(column.getValue(row))).join(',');
      lines.push(line);
    });

    const content = `\uFEFF${lines.join('\n')}`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `metricas_historial_${filters.startDate}_${filters.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <section id="history-metrics-section" className="usage-metrics-section">
      {/* Filters Panel - matching historial tab design */}
      <section className="filters-panel">
        <div className="filters-container">
          <div className="filters-header">
            <h2 className="filters-title">
              <i data-lucide="bar-chart-3" width="18" height="18"></i>
              Métricas del Historial
            </h2>
            <button className="filters-reset-btn" type="button" onClick={handleExport} disabled={loading}>
              <i data-lucide="download" width="16" height="16"></i>
              <span>Exportar</span>
            </button>
          </div>

          <form onSubmit={handleFiltersSubmit}>
            <div className="filters-grid">
              <div className="filter-group date-range-group">
                <label className="filter-label">
                  <i data-lucide="calendar" width="16" height="16"></i>
                  Rango de Fechas
                </label>
                <div className="date-inputs">
                  <input
                    type="date"
                    className="filter-date"
                    value={draftFilters.startDate}
                    onChange={(event) => setFilterValue('startDate', event.target.value)}
                    required
                  />
                  <span className="date-separator">–</span>
                  <input
                    type="date"
                    className="filter-date"
                    value={draftFilters.endDate}
                    onChange={(event) => setFilterValue('endDate', event.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">
                  <i data-lucide="layers" width="16" height="16"></i>
                  División
                </label>
                <select
                  className="filter-select"
                  value={draftFilters.divisionId}
                  onChange={(event) => setFilterValue('divisionId', event.target.value)}
                  disabled={catalogs.loading}
                >
                  <option value="">Todas las divisiones</option>
                  {catalogs.divisions.map((division) => (
                    <option key={division.id} value={division.boh}>
                      {division.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">
                  <i data-lucide="map-pin" width="16" height="16"></i>
                  Área
                </label>
                <select
                  className="filter-select"
                  value={draftFilters.areaId}
                  onChange={(event) => setFilterValue('areaId', event.target.value)}
                  disabled={catalogs.loading || !filteredAreas.length}
                >
                  <option value="">Todas las áreas</option>
                  {filteredAreas.map((area) => (
                    <option key={area.id} value={area.boh}>
                      {area.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="metrics-options-row">
              <div className="metrics-option-group">
                <span className="metrics-option-title">
                  <i data-lucide="package" width="14" height="14"></i>
                  Pronóstico incluido
                </span>
                <div className="metrics-toggles">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={draftFilters.includeArticulos}
                      onChange={(event) => setFilterValue('includeArticulos', event.target.checked)}
                    />
                    <span className="toggle-text">Artículos</span>
                  </label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={draftFilters.includeRecetas}
                      onChange={(event) => setFilterValue('includeRecetas', event.target.checked)}
                    />
                    <span className="toggle-text">Recetas</span>
                  </label>
                </div>
              </div>
              <div className="metrics-option-group">
                <span className="metrics-option-title">
                  <i data-lucide="settings-2" width="14" height="14"></i>
                  Modo de lectura
                </span>
                <div className="metrics-toggles">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={draftFilters.aggregate}
                      onChange={(event) => setFilterValue('aggregate', event.target.checked)}
                    />
                    <span className="toggle-text">Agrupar por usuario</span>
                  </label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={draftFilters.forecastDetail}
                      onChange={(event) => setFilterValue('forecastDetail', event.target.checked)}
                    />
                    <span className="toggle-text">Detalle de pronósticos</span>
                  </label>
                </div>
              </div>
              <button className="apply-filters-btn" type="submit" disabled={loading}>
                <i data-lucide="search" width="16" height="16"></i>
                <span>{loading ? 'Consultando...' : 'Aplicar'}</span>
              </button>
            </div>
          </form>
          {error ? (
            <div className="form-error" role="alert" style={{ marginTop: '16px' }}>{error}</div>
          ) : null}
        </div>
      </section>

      {/* Summary Section - matching results-section design */}
      <section className="results-section" style={{ minHeight: 'auto' }}>
        <div className="results-header">
          <div className="results-info">
            <h2 className="results-title">Resumen</h2>
            <div className="results-count">
              <span className="count-number">{filters.startDate}</span>
              <span className="count-label">→ {filters.endDate}</span>
            </div>
          </div>
        </div>
        <div className="usage-summary-grid">
          <SummaryCard
            label="Clicks totales"
            value={summary.totalClicks.toLocaleString()}
            icon="mouse-pointer-2"
            accent="primary"
          />
          <SummaryCard
            label="Artículos consultados"
            value={summary.articulos.toLocaleString()}
            icon="package"
            accent="soft"
          />
          <SummaryCard
            label="Recetas consultadas"
            value={summary.recetas.toLocaleString()}
            icon="chef-hat"
            accent="soft"
          />
          <SummaryCard
            label="Participación recetas"
            value={`${summary.articulos + summary.recetas > 0 ? Math.round((summary.recetas / (summary.articulos + summary.recetas)) * 100) : 0}%`}
            icon="pie-chart"
            accent="muted"
          />
        </div>
      </section>

      <UsageTrends trends={trends} rows={rows} filters={filters} />

      {/* Table Section - matching results-section design */}
      <section className="results-section">
        <div className="results-header">
          <div className="results-info">
            <h2 className="results-title">Tabla de actividad</h2>
            <div className="results-count">
              <span className="count-number">{filteredRows.length}</span>
              <span className="count-label">registros encontrados</span>
            </div>
          </div>
          <div className="results-controls">
            <div className="search-input-wrapper" style={{ marginRight: '12px' }}>
              <input
                type="text"
                className="filter-search"
                placeholder="Buscar en la tabla..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={handleTableExport} disabled={!paginatedRows.length}>
              <i data-lucide="download" width="16" height="16"></i>
              <span>Exportar CSV</span>
            </button>
          </div>
        </div>
        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Consultando métricas...</div>
            </div>
          ) : (
            <table className="history-table">
              <thead>
                <tr>
                  {tableHeaders.map((header) => <th key={header}>{header}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={tableHeaders.length} className="empty-state">
                      <div className="empty-icon"><i data-lucide="chart-line" /></div>
                      <h3>No hay datos para el rango seleccionado</h3>
                      <p>Intenta ajustando los filtros de búsqueda.</p>
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, index) => (
                    <tr key={index}>
                      {tableColumnsVisible.map((column) => (
                        <td key={column.key}>{column.getValue(row)}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        {rows.length > rowsPerPage ? (
          <section className="table-pagination">
            <div className="pagination-container">
              <button
                className="pagination-btn"
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <i data-lucide="chevron-left" width="16" height="16"></i>
              </button>
              <div className="pagination-info">
                <div className="page-details">
                  Página {currentPage} de {totalPages} • {filteredRows.length.toLocaleString()} registros
                </div>
              </div>
              <button
                className="pagination-btn"
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <i data-lucide="chevron-right" width="16" height="16"></i>
              </button>
            </div>
          </section>
        ) : null}
      </section>
    </section>
  );
};

const UsageSummary = ({ summary, filters }) => {
  const totalPronosticos = summary.articulos + summary.recetas;
  const recetasShare = totalPronosticos > 0 ? Math.round((summary.recetas / totalPronosticos) * 100) : 0;
  const cards = useMemo(() => ([
    {
      label: 'Clicks totales',
      value: summary.totalClicks.toLocaleString(),
      icon: 'mouse-pointer-2',
      accent: 'primary'
    },
    {
      label: 'Artículos consultados',
      value: summary.articulos.toLocaleString(),
      icon: 'package',
      accent: 'soft'
    },
    {
      label: 'Recetas consultadas',
      value: summary.recetas.toLocaleString(),
      icon: 'chef-hat',
      accent: 'soft'
    },
    {
      label: 'Participación recetas',
      value: `${recetasShare}%`,
      icon: 'pie-chart',
      accent: 'muted'
    }
  ]), [summary.totalClicks, summary.articulos, summary.recetas, recetasShare]);

  return (
    <div className="metrics-card summary-card">
      <div className="card-heading">
        <div>
          <div className="section-eyebrow">Resumen</div>
          <h3 className="card-title">Pulso inmediato</h3>
        </div>
        <div className="pill-group compact">
          <span className="pill subtle">Rango</span>
          <span className="pill strong">{filters.startDate} → {filters.endDate}</span>
        </div>
      </div>
      <div className="usage-summary-grid">
        {cards.map((card) => (
          <SummaryCard key={card.label} label={card.label} value={card.value} icon={card.icon} accent={card.accent} />
        ))}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, icon, accent }) => (
  <div className={`summary-card metric-chip ${accent ? `metric-chip-${accent}` : ''}`}>
    <div className="summary-icon">
      <i data-lucide={icon} />
    </div>
    <div className="summary-content">
      <span className="summary-label">{label}</span>
      <span className="summary-value">{value}</span>
    </div>
  </div>
);

const UsageTrends = ({ trends, rows, filters }) => {
  const usersRef = useRef(null);
  const clicksRef = useRef(null);
  const perUserRef = useRef(null);
  const processTypeRef = useRef(null);

  // New Refs
  const topRestaurantsRef = useRef(null);
  const bottomRestaurantsRef = useRef(null);

  /* --- DATA AGGREGATION --- */
  const { top10, bottom10 } = useMemo(() => {
    const restaurantMap = new Map();
    rows.forEach((row) => {
      // Usamos sucursal_nombre. Si no existe, 'Desconocido'
      const name = row.sucursal_nombre || 'Desconocido';
      const count = Number(row.total || 0);
      const nextValue = (restaurantMap.get(name) || 0) + count;
      restaurantMap.set(name, nextValue);
    });

    const restaurantArray = Array.from(restaurantMap.entries()).map(([name, total]) => ({
      name,
      total: Number(total || 0)
    }));

    const sortedDesc = [...restaurantArray].sort(
      (a, b) => (b.total - a.total) || a.name.localeCompare(b.name, 'es')
    );
    const sortedAsc = [...restaurantArray].sort(
      (a, b) => (a.total - b.total) || a.name.localeCompare(b.name, 'es')
    );

    const top10 = sortedDesc.slice(0, 10);
    const bottom10 = sortedAsc.slice(0, 10);

    return { top10, bottom10 };
  }, [rows]);

  /* --- PLOTLY HELPERS --- */
  const safeReact = (node, data, layout) => {
    if (!node || !node.parentNode) return;
    Plotly.react(node, data, layout, { displaylogo: false, responsive: true });
  };

  const safePurge = (node) => {
    if (node && node.parentNode) Plotly.purge(node);
  };

  const baseLayout = useMemo(() => ({
    margin: { l: 40, r: 20, t: 20, b: 40 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    font: { family: 'Sofia Pro, system-ui, sans-serif', size: 12, color: '#3a3f4b' },
    xaxis: { tickfont: { size: 11 }, showgrid: false },
    yaxis: { tickfont: { size: 11 }, gridcolor: 'rgba(58,63,75,0.08)' }
  }), []);

  const buildLayout = (overrides = {}) => {
    const { margin, font, xaxis, yaxis, ...rest } = overrides;
    return {
      ...baseLayout,
      ...rest,
      margin: { ...baseLayout.margin, ...margin },
      font: { ...baseLayout.font, ...font },
      xaxis: { ...baseLayout.xaxis, ...xaxis },
      yaxis: { ...baseLayout.yaxis, ...yaxis }
    };
  };

  const downloadPlot = async (node, filename) => {
    if (!node) return;
    try {
      const dataUrl = await Plotly.toImage(node, { format: 'png', height: 480, width: 960 });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      // console.error('Export failed', err);
    }
  };

  /* --- TIME SERIES CHARTS (Existing) --- */
  useEffect(() => {
    if (!usersRef.current || !clicksRef.current || !perUserRef.current) return;

    // Users
    safeReact(usersRef.current, [{
      x: trends.labels,
      y: trends.uniqueUsers,
      type: 'scatter',
      mode: 'lines+markers+text',
      text: trends.uniqueUsers.map(String),
      textposition: 'top center',
      line: { color: '#c8102e', width: 2, shape: 'spline', smoothing: 1 },
      marker: { size: 6 }
    }], buildLayout({ yaxis: { rangemode: 'tozero', autorange: true } }));

    // Clicks
    safeReact(clicksRef.current, [{
      x: trends.labels,
      y: trends.totalClicks,
      type: 'scatter',
      mode: 'lines+markers+text',
      text: trends.totalClicks.map(String),
      textposition: 'top center',
      line: { color: '#2d3a4b', width: 2, shape: 'spline', smoothing: 1 },
      marker: { size: 6 }
    }], buildLayout({ yaxis: { rangemode: 'tozero', autorange: true } }));

    // Clicks per User
    const perUserSeries = trends.labels.map((_, index) => {
      const u = Number(trends.uniqueUsers[index] || 0);
      const c = Number(trends.totalClicks[index] || 0);
      return u ? Number((c / u).toFixed(2)) : 0;
    });

    safeReact(perUserRef.current, [{
      x: trends.labels,
      y: perUserSeries,
      type: 'scatter',
      mode: 'lines+markers+text',
      text: perUserSeries.map(String),
      textposition: 'top center',
      line: { color: '#586174', width: 2, shape: 'spline', smoothing: 1 },
      marker: { size: 6 }
    }], buildLayout({ yaxis: { rangemode: 'tozero', autorange: true } }));

    return () => {
      safePurge(usersRef.current);
      safePurge(clicksRef.current);
      safePurge(perUserRef.current);
    };
  }, [trends.labels, trends.totalClicks, trends.uniqueUsers, baseLayout]);

  /* --- NEW CHARTS: Top 10, Bottom 10 --- */
  useEffect(() => {
    const layoutDetails = {
      xaxis: { rangemode: 'tozero' },
      yaxis: { autorange: 'reversed' },
      showlegend: false
    };

    // Helper for horizontal bars
    const renderHBar = (node, dataArr, title, color) => {
      if (!node) return;
      const xValues = dataArr.map((item) => item.total);
      const yValues = dataArr.map((item) => item.name);

      const trace = {
        type: 'bar',
        orientation: 'h',
        x: xValues,
        y: yValues,
        text: xValues.map((value) => Number(value).toLocaleString()),
        textposition: 'auto',
        marker: { color: color, opacity: 0.8 },
        hoverinfo: 'y+x'
      };

      const h = Math.max(300, dataArr.length * 35 + 50);
      const dynamicLayout = buildLayout({
        ...layoutDetails,
        title: { text: title, font: { size: 14 } },
        margin: { l: 220, t: 40 },
        height: h
      });

      safeReact(node, [trace], dynamicLayout);
    };

    renderHBar(topRestaurantsRef.current, top10, 'Top 10: Mayor Generación', '#28A745');
    renderHBar(bottomRestaurantsRef.current, bottom10, 'Bottom 10: Menor Generación', '#C8102E');

    return () => {
      safePurge(topRestaurantsRef.current);
      safePurge(bottomRestaurantsRef.current);
    };
  }, [top10, bottom10, baseLayout]);

  /* --- PROCESS TYPE CHART (Keep) --- */
  useEffect(() => {
    if (!processTypeRef.current || !trends.processTypeBreakdown) return;
    const { articulos = [], recetas = [] } = trends.processTypeBreakdown;

    const safePurge = (node) => {
      if (node && node.parentNode) {
        Plotly.purge(node);
      }
    };

    const enabledTypes = [
      { key: 'articulos', label: 'Artículos', enabled: filters.includeArticulos, data: articulos },
      { key: 'recetas', label: 'Recetas', enabled: filters.includeRecetas, data: recetas }
    ].filter((type) => type.enabled);

    const hasData = enabledTypes.some((type) => type.data.length > 0);

    if (!enabledTypes.length || !hasData) {
      safePurge(processTypeRef.current);
      return;
    }

    const labels = enabledTypes.map((type) => type.label);
    const allTypes = new Set(enabledTypes.flatMap((type) => type.data.map((item) => item.label)));
    const colors = ['#c8102e', '#f4b6c2', '#d24159', '#586174', '#2d3a4b', '#e8a0ae'];

    const traces = Array.from(allTypes).map((pt, i) => {
      const counts = enabledTypes.map((type) => {
        const hit = type.data.find((entry) => entry.label === pt);
        return hit ? hit.count || 0 : 0;
      });
      return {
        y: labels,
        x: counts,
        name: pt,
        type: 'bar',
        orientation: 'h',
        marker: { color: colors[i % colors.length] },
        text: counts.map((value) => value > 0 ? String(value) : ''),
        textposition: 'inside',
        textfont: { color: '#fff', size: 11 },
        hovertemplate: `<b>${pt}</b><br>Cantidad: %{x}<extra></extra>`
      };
    });

    safeReact(processTypeRef.current, traces, buildLayout({
      height: 180,
      barmode: 'stack',
      showlegend: true,
      yaxis: { autorange: 'reversed' },
      legend: { orientation: 'h', x: 0, y: 1.2, font: { size: 10 } }
    }));

    return () => safePurge(processTypeRef.current);
  }, [filters.includeArticulos, filters.includeRecetas, trends.processTypeBreakdown, baseLayout]);

  return (
    <section className="results-section">
      <div className="results-header">
        <div className="results-info">
          <h2 className="results-title">Tendencias</h2>
          <div className="results-count">
            <span className="count-label">{filters.startDate} → {filters.endDate}</span>
          </div>
        </div>
      </div>

      <div className="metrics-trends-grid">
        {/* Time Series */}
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Usuarios únicos</div>
            <button className="btn-ghost btn-mini" onClick={() => downloadPlot(usersRef.current, 'usuarios_unicos.png')}>Descargar PNG</button>
          </div>
          <div className="trend-chart" ref={usersRef} />
        </div>
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Cantidad de clics</div>
            <button className="btn-ghost btn-mini" onClick={() => downloadPlot(clicksRef.current, 'cantidad_clics.png')}>Descargar PNG</button>
          </div>
          <div className="trend-chart" ref={clicksRef} />
        </div>
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Pronósticos por usuario</div>
            <button className="btn-ghost btn-mini" onClick={() => downloadPlot(perUserRef.current, 'pronosticos_por_usuario.png')}>Descargar PNG</button>
          </div>
          <div className="trend-chart" ref={perUserRef} />
        </div>

        {/* NEW Charts */}
        <div className="trend-panel span-full">
          <div className="trend-header">
            <div className="trend-title">Top 10 Generación</div>
            <button className="btn-ghost btn-mini" onClick={() => downloadPlot(topRestaurantsRef.current, 'top10_restaurantes.png')}>Descargar PNG</button>
          </div>
          <div className="trend-chart" ref={topRestaurantsRef} />
        </div>

        <div className="trend-panel span-full">
          <div className="trend-header">
            <div className="trend-title">Bottom 10 Generación</div>
            <button className="btn-ghost btn-mini" onClick={() => downloadPlot(bottomRestaurantsRef.current, 'bottom10_restaurantes.png')}>Descargar PNG</button>
          </div>
          <div className="trend-chart" ref={bottomRestaurantsRef} />
        </div>

        {/* Process Types */}
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Procesos seleccionados</div>
            <button className="btn-ghost btn-mini" onClick={() => downloadPlot(processTypeRef.current, 'procesos_seleccionados.png')}>Descargar PNG</button>
          </div>
          {(filters.includeArticulos && trends.processTypeBreakdown?.articulos?.length) ||
          (filters.includeRecetas && trends.processTypeBreakdown?.recetas?.length) ? (
            <div className="trend-chart dynamic-height" ref={processTypeRef} />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon"><i data-lucide="layers" /></div>
              <div className="empty-state-text">Sin datos de procesos seleccionados.</div>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default HistoryMetricsSection;
